import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthenticatedResponse, SessionUser } from '@nlinks/shared';
import { api, getRefreshToken, onAuthChange, setTokens } from '@/lib/api';

interface AuthState {
  user: SessionUser | null;
  /** True while the initial session restore is running. */
  initializing: boolean;
  signIn: (response: AuthenticatedResponse) => void;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setUser: (user: SessionUser) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<SessionUser | null>(null);
  const [initializing, setInitializing] = useState(true);
  const queryClient = useQueryClient();

  // Restore the session from the stored refresh token before the first render
  // of any protected route — otherwise a reload bounces the user to /login.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!getRefreshToken()) {
        setInitializing(false);
        return;
      }
      try {
        const token = await api.bootstrap();
        if (token && !cancelled) {
          const me = await api.get<SessionUser>('/api/auth/me');
          if (!cancelled) setUserState(me);
        }
      } catch {
        setTokens(null);
      } finally {
        if (!cancelled) setInitializing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // The api layer clears tokens when a refresh finally fails; mirror that into
  // React state so the router redirects instead of rendering an empty shell.
  useEffect(
    () =>
      onAuthChange((authenticated) => {
        if (!authenticated) {
          setUserState(null);
          queryClient.clear();
        }
      }),
    [queryClient],
  );

  const signIn = useCallback((response: AuthenticatedResponse) => {
    setTokens({ accessToken: response.accessToken, refreshToken: response.refreshToken });
    setUserState(response.user);
  }, []);

  const signOut = useCallback(async () => {
    const refreshToken = getRefreshToken();
    try {
      if (refreshToken) await api.post('/api/auth/logout', { refreshToken });
    } catch {
      // Logging out locally matters more than the server acknowledging it.
    } finally {
      setTokens(null);
      setUserState(null);
      queryClient.clear();
    }
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    const me = await api.get<SessionUser>('/api/auth/me');
    setUserState(me);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, initializing, signIn, signOut, refreshUser, setUser: setUserState }),
    [user, initializing, signIn, signOut, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Throws if called outside an authenticated route — keeps pages free of null checks. */
export function useCurrentUser(): SessionUser {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser called without an authenticated session');
  return user;
}
